<template>
  <div class="voices-container">
    <div class="content-area">
      <div class="page-header">
        <h1 class="page-title">
          Voices
        </h1>
        <div class="header-actions">
          <v-btn
            color="primary"
            class="mank-btn"
            @click="createVoice"
            elevation="0">
            <Icon name="plus" variant="solid" color="onPrimary"/>
            Create Voice
          </v-btn>
        </div>
      </div>

      <!-- Main Content -->
      <div class="main-content">
        <!-- Tabs Area -->
        <div class="tabs-area mank-tabs-strip">
          <div class="tabs-container">
            <v-btn
              :color="activeTab === 'custom' ? 'primary' : 'grey-lighten-2'"
              :variant="activeTab === 'custom' ? 'flat' : 'text'"
              size="small"
              elevation="0"
              @click="activeTab = 'custom'"
              class="tab-button"
              :class="{ 'active-tab': activeTab === 'custom' }">
              Custom
            </v-btn>
            <v-btn
              :color="activeTab === 'builtin' ? 'primary' : 'grey-lighten-2'"
              :variant="activeTab === 'builtin' ? 'flat' : 'text'"
              size="small"
              elevation="0"
              @click="activeTab = 'builtin'"
              class="tab-button"
              :class="{ 'active-tab': activeTab === 'builtin' }">
              Built-In
            </v-btn>
          </div>
        </div>

        <!-- Table Area -->
        <div class="table-area table-area--pad table-area--scroll-y">
          <!-- Custom Tab Content -->
          <div v-if="activeTab === 'custom'">
            <!-- Empty State for Custom Tab -->
            <div v-if="customVoices.length === 0" class="empty-state text-center">
              <div class="empty-state-icon mb-4">
                <div class="icon-circle">
                  <v-icon size="40" color="primary">
                    mdi-microphone
                  </v-icon>
                </div>
              </div>
              <div class="empty-state-text mb-4">
                <h3 class="ds-panel-title mb-1">
                  No Custom Voice Created
                </h3>
                <p class="text-body-2 text--secondary">
                  You can create your own unique voice by clicking the button below.
                </p>
              </div>
              <v-btn
                color="primary"
                variant="flat"
                size="small"
                elevation="0"
                @click="createVoice">
                <v-icon start>
                  mdi-plus
                </v-icon>
                Create Voice
              </v-btn>
            </div>

            <!-- Custom Voices Data Table -->
            <div v-else class="voices-table">
              <v-data-table
                :items-per-page="selectedItemsPerPage"
                :page="page"
                :headers="headers"
                :items="customVoices"
                :search="searchQuery"
                :loading="loading"
                class="voices-data-table"
                item-value="id"
                no-data-text="No voices found"
                loading-text="Loading voices..."
                items-per-page-text="Voices per page:"
                hide-default-footer
                show-current-page>
                <template v-slot:header.name="{ header }">
                  {{ header.text.toUpperCase() }}
                </template>

                <!-- Voice Name Column -->
                <template v-slot:item.name="{ item }">
                  <div class="d-flex align-center">
                    <div class="text-element-2 text-bold">
                      {{ item.name }}
                    </div>
                  </div>
                </template>

                <!-- Description Column -->
                <template v-slot:item.description="{ item }">
                  <div class="text-element-2">
                    {{ item.description }}
                  </div>
                </template>

                <!-- ID Column with Copy Button -->
                <template v-slot:item.id="{ item }">
                  <div class="id-column d-flex align-center">
                    <span class="text-element-2 id-text">{{ item.id }}</span>
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      @click="copyId(item.id)"
                      class="copy-btn ml-2">
                      <v-icon size="12">
                        mdi-content-copy
                      </v-icon>
                      <v-tooltip activator="parent" location="top">
                        Copy ID
                      </v-tooltip>
                    </v-btn>
                  </div>
                </template>

                <!-- Actions Column -->
                <template v-slot:item.sample="{ item }">
                  <div class="action-buttons d-flex align-center gap-1">
                    <v-btn
                      icon
                      @click="playSample(item)"
                      class="delete-btn mank-btn-icon mr-4">
                      <Icon name="play" variant="solid" color="default" size="16"/>
                    </v-btn>
                    <v-btn
                      icon
                      @click="confirmDeleteVoice(item)"
                      class="delete-btn mank-btn-icon ">
                      <Icon name="trash" variant="solid" color="default" size="16"/>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>

              <div class="table-footer">
                <v-pagination
                  v-model="page"
                  :total-visible="6"
                  :length="totalPages"/>
                <div class="pagination-info ml-auto mr-0">
                  <span class="text-caption-1 text--secondary">
                    Showing
                  </span>
                  <v-select
                    class="items-per-page-select"
                    :items="itemsPerPage"
                    v-model="selectedItemsPerPage"
                    hide-details
                    outlined>
                    <template v-slot:append>
                      <Icon name="expand-down" variant="solid" color="muted" size="16"/>
                    </template>
                  </v-select>
                  <span class="text-caption-1 text--secondary">
                    / {{ totalItems }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Built-in Tab Content -->
          <div v-else-if="activeTab === 'builtin'" class="voices-table">
            <v-data-table
              :items-per-page="selectedItemsPerPage"
              :page="page"
              :headers="headers"
              :items="builtInVoices"
              :search="searchQuery"
              :loading="loading"
              class="voices-data-table"
              item-value="id"
              no-data-text="No voices found"
              loading-text="Loading voices..."
              items-per-page-text="Voices per page:"
              hide-default-footer
              show-current-page>
              <template v-slot:header.name="{ header }">
                {{ header.text.toUpperCase() }}
              </template>

              <!-- Voice Name Column -->
              <template v-slot:item.name="{ item }">
                <div class="d-flex align-center">
                  <div class="text-element-2 text-bold">
                    {{ item.name }}
                  </div>
                </div>
              </template>

              <!-- Description Column -->
              <template v-slot:item.description="{ item }">
                <div class="text-element-2">
                  {{ item.description }}
                </div>
              </template>

              <!-- ID Column with Copy Button -->
              <template v-slot:item.id="{ item }">
                <div class="id-column d-flex align-center">
                  <span class="text-element-2 id-text">{{ item.id }}</span>
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    @click="copyId(item.id)"
                    class="copy-btn ml-2">
                    <v-icon size="12">
                      mdi-content-copy
                    </v-icon>
                    <v-tooltip activator="parent" location="top">
                      Copy ID
                    </v-tooltip>
                  </v-btn>
                </div>
              </template>

              <!-- Actions Column (Built-in: delete sadece admin) -->
              <template v-slot:item.sample="{ item }">
                <div class="action-buttons d-flex align-center gap-1">
                  <v-btn
                    icon
                    @click="playSample(item)"
                    class="delete-btn mank-btn-icon mr-4"
                    title="Play sample">
                    <Icon name="play" variant="solid" color="default" size="16"/>
                  </v-btn>
                  <v-btn
                    v-if="isSuperuser"
                    icon
                    @click="confirmDeleteVoice(item)"
                    class="delete-btn mank-btn-icon"
                    title="Delete voice">
                    <Icon name="trash" variant="solid" color="default" size="16"/>
                  </v-btn>
                </div>
              </template>
            </v-data-table>

            <div class="table-footer">
              <v-pagination
                v-model="page"
                :total-visible="6"
                :length="totalPages"/>
              <div class="pagination-info ml-auto mr-0">
                <span class="text-caption-1 text--secondary">
                  Showing
                </span>
                <v-select
                  class="items-per-page-select"
                  :items="itemsPerPage"
                  v-model="selectedItemsPerPage"
                  hide-details
                  outlined>
                  <template v-slot:append>
                    <Icon name="expand-down" variant="solid" color="muted" size="16"/>
                  </template>
                </v-select>
                <span class="text-caption-1 text--secondary">
                  / {{ totalItems }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmationModal
      :visible="showDeleteDialog"
      @cancel="cancelDelete"
      @confirm="confirmDelete"/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Icon from '@/components/Icon.vue';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue';
import VoiceService from '@/services/VoiceService';

export default {
  name: 'Voices',
  components: {
    Icon,
    DeleteConfirmationModal,
  },
  data() {
    return {
      searchQuery: '',
      activeTab: 'custom',
      page: 1,
      selectedItemsPerPage: 10,
      loading: false,
      voices: [],
      showDeleteDialog: false,
      voiceToDelete: null,
      headers: [
        {
          text: 'Name',
          align: 'start',
          sortable: true,
          value: 'name',
        },
        {
          text: 'Description',
          align: 'start',
          sortable: false,
          value: 'description',
        },
        {
          text: 'ID',
          align: 'start',
          sortable: false,
          value: 'id',
        },
        {
          text: 'Actions',
          sortable: false,
          value: 'sample',
        },
      ],
      itemsPerPage: [5, 10, 20, 50, 100],
    };
  },
  computed: {
    ...mapGetters('auth', ['isSuperuser']),
    customVoices() {
      return this.voices.filter((voice) => voice.type === 'custom');
    },
    builtInVoices() {
      return this.voices.filter((voice) => voice.type === 'builtin');
    },
    totalPages() {
      const currentVoices = this.activeTab === 'builtin' ? this.builtInVoices : this.customVoices;
      return Math.ceil(currentVoices.length / this.selectedItemsPerPage);
    },
    totalItems() {
      const currentVoices = this.activeTab === 'builtin' ? this.builtInVoices : this.customVoices;
      return currentVoices.length;
    },
  },
  watch: {
    activeTab() {
      this.page = 1;
    },
    searchQuery() {
      this.page = 1;
    },
  },
  async mounted() {
    await this.fetchVoices();
  },
  methods: {
    async fetchVoices() {
      this.loading = true;
      try {
        const response = await VoiceService.getAllVoices();
        const raw = response.data || [];

        // API'den gelen type'a göre builtin / custom ayrımı; yoksa custom say
        this.voices = raw.map((voice) => ({
          ...voice,
          type: voice.type === 'builtin' ? 'builtin' : 'custom',
        }));
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to fetch voices',
        });
      } finally {
        this.loading = false;
      }
    },
    createVoice() {
      this.$router.push({ name: 'CreateVoice' });
    },
    async copyId(id) {
      try {
        await navigator.clipboard.writeText(id);
        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Copied',
          message: 'Voice ID copied to clipboard',
        });
      } catch (error) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: 'Failed to copy ID',
        });
      }
    },
    async playSample(voice) {
      // Sadece custom seslerin Asset'i var; built-in'de play endpoint 404 döner
      if (voice.type !== 'custom') {
        this.$store.dispatch('ui/showNotification', {
          type: 'warning',
          title: 'No Sample',
          message: 'No audio sample available for this voice',
        });
        return;
      }
      try {
        const blob = await VoiceService.getVoicePlayBlob(voice.id);
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audio.onended = () => URL.revokeObjectURL(url);
        audio.onerror = () => {
          URL.revokeObjectURL(url);
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            title: 'Error',
            message: 'Failed to play voice sample',
          });
        };
        await audio.play();
      } catch (err) {
        const message = err?.response?.status === 404
          ? 'No audio sample available for this voice'
          : 'Failed to play voice sample';
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message,
        });
      }
    },
    confirmDeleteVoice(voice) {
      if (voice.type === 'builtin' && !this.isSuperuser) {
        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Yetkisiz',
          message: 'Built-in voice silme işlemi sadece superuser kullanıcılar tarafından yapılabilir.',
        });
        return;
      }
      this.voiceToDelete = voice;
      this.showDeleteDialog = true;
    },
    cancelDelete() {
      this.showDeleteDialog = false;
      this.voiceToDelete = null;
    },
    async confirmDelete() {
      if (this.voiceToDelete) {
        if (this.voiceToDelete.type === 'builtin' && !this.isSuperuser) {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            title: 'Yetkisiz',
            message: 'Built-in voice silme işlemi sadece superuser kullanıcılar tarafından yapılabilir.',
          });
          this.showDeleteDialog = false;
          this.voiceToDelete = null;
          return;
        }
        try {
          await VoiceService.deleteVoice(this.voiceToDelete.id);

          this.$store.dispatch('ui/showNotification', {
            type: 'success',
            title: 'Success',
            message: `Voice "${this.voiceToDelete.name}" deleted successfully`,
          });

          await this.fetchVoices(); // Listeyi yenile
        } catch (error) {
          this.$store.dispatch('ui/showNotification', {
            type: 'error',
            title: 'Error',
            message: 'Failed to delete voice',
          });
        }
      }
      this.showDeleteDialog = false;
      this.voiceToDelete = null;
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/variables.scss";
@import "../styles/colors.scss";

.page-header .header-actions {
    .search-input {
      width: 200px;

      .custom-search {
        .v-field {
          background-color: $light-surface-1; // #F7F8FB
          border-radius: 8px;
          font-family: "Plus Jakarta Sans", sans-serif;
          font-size: $body-3-size;

          .v-field__input {
            color: $light-foreground-1; // #171D22

            &::placeholder {
              color: $light-foreground-4; // #93A0AF
            }
          }

          .v-field__outline {
            border-color: transparent;
          }

          &--focused .v-field__outline {
            border-color: $light-primary; // #EB575D
            border-width: 1px;
          }
        }
      }
    }

    .create-btn {
      height: 32px;
      font-family: "Plus Jakarta Sans", sans-serif;
      font-weight: 600;
      font-size: $body-3-size;
      background-color: $light-primary; // #EB575D
      color: $light-surface-2; // #FFFFFF

      &:hover {
        background-color: darken($light-primary, 5%);
    }
  }
}

.main-content {
  gap: 10px;
  overflow-y: auto;
}

// V-Data-Table Styles (copied from RAG.vue)
.voices-table {
  .voices-data-table {
    background-color: transparent;

    :deep(.v-table) {
      background-color: transparent;
    }

    :deep(.v-table__wrapper) {
      border-radius: 8px;
      border: 1px solid $light-border;
    }

    :deep(.v-data-table-header) {
      background-color: $light-surface-1;
    }

    :deep(.v-data-table-header__content) {
      font-family: $body-font-family;
      font-weight: 600;
      font-size: $body-3-size;
      color: $light-foreground-4;
    }

    :deep(.v-data-table__td) {
      border-bottom: 1px solid $light-border;
      padding: 12px 16px;

      .text-element-2 {
        font-family: $body-font-family;
        font-size: $body-3-size;
        color: $light-foreground-1;

        &.text-bold {
          font-weight: 600;
        }
      }
    }

    :deep(.v-data-table__tr:hover) {
      background-color: $light-surface-1;
    }
  }
}

.id-column {
  max-width: 200px;

  .id-text {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .copy-btn {
    opacity: 0.6;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
      background-color: $light-surface-2;
    }
  }
}

.table-footer {
  background: $light-surface-2;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  border-top: 1px solid $light-border;
  height: 48px;
}

.pagination-info {
  font-size: $caption-1-size;
  color: $light-foreground-2;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.items-per-page-select {
  width: 48px;
  height: 24px;
  min-height: 24px;
  max-height: 24px;
}

// Cursor styles
.cursor-pointer {
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

// Pagination styles (copied from RAG.vue)
::v-deep .v-pagination .v-pagination__navigation {
  box-shadow: none;
  border: none;
}

::v-deep .v-pagination .v-pagination__item {
  box-shadow: none;
  border: none;
  font-weight: 600;
  width: 24px;
  height: 24px;
  min-height: 24px;
  max-height: 24px;
  min-width: 24px;
}

::v-deep .v-pagination .v-pagination__item--active {
  background-color: transparent !important;
  color: $light-primary;
  border: 1px solid $light-primary-neutral;
}

::v-deep .v-data-table-header {
  background-color: $light-surface-1;
}

::v-deep .v-data-table-header tr th {
  color: $light-foreground-3 !important;
  font-weight: 600;
  font-size: $body-3-size !important;
  text-transform: capitalize;
}

// Empty State Styles
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 48px 24px;

  .empty-state-icon {
    .icon-circle {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      background-color: $light-surface-1;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .empty-state-text {
    h3 {
      font-family: $body-font-family;
      font-weight: 700;
      font-size: $h5-size;
      color: $light-foreground-1;
      margin: 0;
    }

    p {
      font-family: $body-font-family;
      font-size: $body-3-size;
      color: $light-foreground-4;
      margin: 0;
    }
  }
}
</style>
